import { z } from "zod";

import {
  RoleBoardAccentSchema,
  RoleBoardCardTypeSchema,
  RoleBoardNameSchema
} from "../../../../shared/domain/roles/role-board.types";

/**
 * Board schemas — the Overview screen's lanes and the card placements in them.
 */

/**
 * A board is a VISUAL GROUPING of the systems inside one Role and nothing else.
 * No permission meaning, no execution meaning: nothing in the access path, the
 * runtime or the coverage engine reads this table.
 *
 * ── EVERY ID ON THIS SURFACE IS uuid-CHECKED ───────────────────────────────
 *
 * `RoleBoard.id` is `@db.Uuid` and `boardId` now says so. That was only ever
 * available as a manifest-wide change (NEX-3552): uuid-checking `boardId` forces
 * `id` across every Role route, which in turn forces
 * `RoleMemberPathVarsSchema.userId` — a Clerk id that is genuinely never a uuid —
 * into an allowlist. A boards slice was the wrong place to move that, so it moved
 * as one coherent change instead. `cardId` remains bare and allowlisted: it is
 * polymorphic over eight card kinds and not every one was proven to be a uuid.
 *
 * The shape check is not skipped, it MOVED: the use cases run `isUUID` before
 * touching Prisma and answer 404. That is the semantically right answer anyway —
 * a malformed id cannot name a row, and it is the same answer a well-formed id
 * for a board in another Role gets. Without it Prisma raises `P2023` and the
 * caller is told the SERVER broke.
 */
export const RoleBoardSchema = z.object({
  id: z.string(),
  roleId: z.string(),
  name: z.string(),
  position: z.number().int(),
  accent: RoleBoardAccentSchema,
  createdAt: z.string(),
  updatedAt: z.string()
});

export type RoleBoard = z.infer<typeof RoleBoardSchema>;

/**
 * One card and the lane it sits in.
 *
 * ADDRESSED BY `(cardType, cardId)` — THE RESOURCE'S OWN IDENTITY, NEVER THE
 * PLACEMENT ROW'S ID. That is not a preference; it is the pair the client
 * already holds. The Overview screen keys every node on
 * `` `${type}:${id}` `` (`systemNodeKey`), the five ownable
 * `RoleSystemNodeType` values are byte-identical to `RoleResourceType` precisely
 * so a node joins straight onto a `RoleResource` row, and NO response in this
 * manifest carries a `RoleResource.id` — `RoleResourceRefSchema` is
 * `{ resourceType, resourceId }`. Addressing a card by its row id would oblige
 * every consumer to fetch a translation table first, to learn an id that means
 * nothing to it, in order to move a card it can already name.
 *
 * So `cardId` IS the resource's id: an `Agent.id` for `cardType: "agent"`, a
 * `Collection.id` for `"collection"`, a `Workspace.id` for `"workspace"`. The
 * pair is a KEY in all three tables —
 * `RoleResource @@unique([organizationId, resourceType, resourceId])`,
 * `RoleCollectionGrant @@unique([roleId, collectionId])`,
 * `RoleWorkspaceGrant @@unique([roleId, workspaceId])` — so `cardType` alone
 * decides which table to read, and the shape stays FLAT: no discriminated union
 * is needed when every arm would carry the identical two fields.
 *
 * It is spelled `cardId` and not `resourceId` for a mechanical reason as well as
 * a naming one. `resourceId` is already uuid-checked elsewhere in this manifest,
 * and `id-validation-consistency.test.ts` holds a field NAME to one verdict
 * across the whole manifest — so reusing the name would demand `.uuid()` here,
 * which would then mix with the bare `:id` in the same PathVars object and trip
 * the sibling rule, which would demand uuid-checking `id` on all eight existing
 * Role routes, which would drag `RoleMemberPathVarsSchema.userId` — a Clerk id
 * that is genuinely never a uuid — into an allowlist. A boards slice is the
 * wrong place to move that. A distinct name is honest here anyway: a card id is
 * not always a `RoleResource.resourceId`.
 *
 * `boardId: null` IS THE UNGROUPED LANE, and it is an absence rather than a
 * seeded default board — which is what makes that lane undeletable and
 * unrenameable for free. Ungrouped cards ARE returned, because the screen has to
 * render them; a reader filtering them out would show a Role missing most of its
 * systems.
 *
 * ⚠️ UNGROUPED IS NOT UNASSIGNED, and the two words must not be traded. UNGROUPED
 * is "this Role holds it, nobody has put it in a lane" — tidy-up, no defect.
 * UNASSIGNED is the Overview screen's existing lane for "no Role holds it at
 * all" — a real configuration gap. They are different facts about different
 * populations and sharing a word would make a clean Role read as a broken one.
 *
 * `cardId` is deliberately NOT uuid-checked, exactly as `RoleResourceRefSchema`
 * is not: `RoleResource.resourceId` is a loose TEXT column with no foreign key,
 * and tightening a READ turns one legacy row into a failed listing for the whole
 * Role. The write path guards the shape PER KIND instead — `Collection.id` and
 * `Workspace.id` are `@db.Uuid`, so a malformed one there is a 404 rather than a
 * `P2023`-shaped 500, while the five resource kinds stay permissive so a legacy
 * non-uuid card is still draggable.
 */
export const RoleBoardCardSchema = z.object({
  cardType: RoleBoardCardTypeSchema,
  cardId: z.string(),
  boardId: z.string().nullable()
});

export type RoleBoardCard = z.infer<typeof RoleBoardCardSchema>;

/**
 * BOARDS AND PLACEMENTS IN ONE ANSWER, ON PURPOSE.
 *
 * The Overview screen renders lanes and the cards in them together or it renders
 * neither, so splitting this into "list boards" plus one call per lane would
 * make the screen's first paint depend on N round trips and give it N+1 chances
 * to show a half-built board. It also could not represent the Ungrouped lane
 * without a special case, since that lane has no board to enumerate.
 *
 * It carries PLACEMENT ONLY — no resource names, types beyond the discriminant,
 * icons or statuses. The screen already holds that metadata from the reads that
 * populate the Role's tabs; re-serving it here would be a second source of truth
 * for a name, and the two would drift the first time either side gained a field.
 *
 * `boards` is ordered by (position, createdAt, id). That order is TOTAL, which
 * is why `position` carries no unique constraint: a reorder rewrites the whole
 * list in one statement and a unique index would collide with itself
 * mid-statement. A duplicate position is therefore untidy, never ambiguous.
 */
export const RoleBoardsViewSchema = z.object({
  boards: z.array(RoleBoardSchema),
  cards: z.array(RoleBoardCardSchema)
});

export type RoleBoardsView = z.infer<typeof RoleBoardsViewSchema>;

export const RoleBoardPathVarsSchema = z.object({
  id: z.string().uuid(),
  boardId: z.string().uuid()
});

export const RoleBoardCardPathVarsSchema = z.object({
  id: z.string().uuid(),
  cardType: RoleBoardCardTypeSchema,
  cardId: z.string()
});

/**
 * `accent` is optional and defaults server-side to `ROLE_BOARD_DEFAULT_ACCENT`,
 * spelled once in `role-board.types.ts` and deliberately NOT a column DEFAULT —
 * a DB default would put the palette's identity in two places that drift
 * silently.
 *
 * `position` is NOT accepted. A new board is appended, and ordering is asserted
 * by `ReorderBoards` over the whole list. Letting a create name its own position
 * would make two ways to order boards, one of which cannot renumber its
 * neighbours.
 */
export const RoleBoardCreateBodySchema = z.object({
  name: RoleBoardNameSchema,
  accent: RoleBoardAccentSchema.optional()
});

/**
 * Rename, recolour, or both. Both optional, and an empty body is a no-op rather
 * than an error — `PATCH` means "change what I named".
 *
 * `position` is absent here for the same reason it is absent from create.
 */
export const RoleBoardUpdateBodySchema = z.object({
  name: RoleBoardNameSchema.optional(),
  accent: RoleBoardAccentSchema.optional()
});

/**
 * REORDER IS AN ASSERTION ABOUT THE WHOLE LIST, WHICH IS WHY IT TAKES THE WHOLE
 * LIST AND NOT A (boardId, position) PAIR.
 *
 * The server renumbers from 0 in the order given, in ONE statement. Six single
 * updates would interleave under concurrency into an arrangement neither caller
 * asked for; one statement means the result is always exactly one of the orders
 * that was submitted.
 *
 * The submitted set must EQUAL the Role's current board set or the write is
 * refused `409 ROLE_BOARD_ORDER_STALE`. Silently renumbering a stale list is the
 * failure this refuses: a board created or deleted by someone else while this
 * list was on screen would be left at a position nobody chose, and the caller
 * would be told it succeeded.
 */
export const RoleBoardReorderBodySchema = z.object({
  /**
   * Distinct is a 400, not a 409: a list naming the same board twice was never
   * a valid order and no refetch fixes it, so it is the caller's bug rather than
   * a race.
   *
   * `ReorderRoleBoardsUseCase` refuses it a SECOND time, and the duplication is
   * deliberate rather than an oversight. The use case is reachable in-process by
   * anything, not only through this contract, and a domain object that is
   * correct BECAUSE its caller validated is correct by coincidence. Two
   * mechanisms that both REFUSE cannot disagree in the dangerous direction — the
   * rule against two mechanisms granting the same thing is about grants.
   *
   * Both spell it as a 400 and neither folds it into the 409 staleness check, so
   * a caller is never told to retry a request that will fail identically for
   * ever.
   */
  boardIds: z
    .array(z.string())
    .refine((ids) => new Set(ids).size === ids.length, "Board ids must be distinct")
});

/**
 * `null` moves the card to the Ungrouped lane. It is a legal destination, not a
 * missing value, so the field is REQUIRED and nullable rather than optional —
 * `{}` must not silently mean "unplace it".
 */
export const RoleBoardCardMoveBodySchema = z.object({
  boardId: z.string().uuid().nullable()
});

/** Delete is idempotent in its effect but not in its answer: `cardsUnplaced` says what fell to Ungrouped. */
export const RoleBoardDeleteResponseSchema = z.object({
  deleted: z.literal(true),
  cardsUnplaced: z.number().int()
});
